import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ProfileGamification } from './ProfileGamification';
import { ProfileAchievements } from './ProfileAchievements';
import { RadioSettingsPanel } from './RadioPlayer';
import { TreasureCollection } from './TreasureCollection';
import { TotalPfandBox } from './TotalPfandBox';
import { PfandOverview } from './PfandOverview';
import { useToast } from '../hooks/useToast';
import { useXpBonus, XP_EVENTS } from '../hooks/useXpBonus';
import { markChestAsOpened } from './TreasureCollection';
import { Link } from 'react-router-dom';
import { Spinner } from './Spinner';
import { useGamification } from '../hooks/GamificationContext';
import { useOptionalAppSession } from '../hooks/AppSessionContext';
import AvatarPickerModal from './profile/AvatarPickerModal';
import PlayerAvatarImage from './profile/PlayerAvatarImage';
import ProfileVisitsCard from './profile/ProfileVisitsCard';
import ReceiptRedemptionModal from './ReceiptRedemptionModal';
import { updateReceiptRedemptionBeta } from '../api/redemptionsApi';
import {
  classifyReceiptRedemption,
  getReceiptRedemptionMessage,
  getReceiptRedeemedAmount,
} from '../utils/receiptRedemptionStatus';
import { useAvatarWardrobe } from '../hooks/useAvatarWardrobe';
import { useProfileVisits } from '../hooks/useProfileVisits';
import ReferralCard from './profile/ReferralCard';
import CollectionSummaryCard from './collection/CollectionSummaryCard';
import { useReferrals } from '../hooks/useReferrals';
import { useAchievements } from '../hooks/useAchievements';
import {
  getConfiguredOneSignalAppId,
  getOneSignalAppStatus,
  setOneSignalPushSubscriptionEnabled,
} from '../utils/onesignal';
import {
  isChallengeFocusEnabled,
  isKorkVoiceEnabled,
  setChallengeFocusEnabled,
  setKorkVoiceEnabled,
} from '../utils/settings';
import '../styles/profile-redesign.css';

// ---- Einheitliche Konfiguration aus wp_localize_script ----
const NONCE = window?.pfandpiratData?.nonce ?? '';
const PUBLIC_PROFILE_SETTINGS_API =
  window?.pfandpiratData?.publicProfileSettingsApiUrl ??
  '/wp-json/pfandpirat/v1/me/public-profile-settings';
const ONESIGNAL_TEST_PUSH_API =
  window?.pfandpiratData?.oneSignalTestPushApiUrl ??
  '/wp-json/pfandpirat/v1/push/test';

/**
 * Öffentliche SEO-Profile laufen jetzt auf der Hauptdomain:
 * https://pfandpirat.com/piraten/{slug}
 *
 * Der Fallback greift nur, wenn public_url aus der API noch nicht da ist.
 */
const PUBLIC_PROFILE_BASE_URL =
  window?.pfandpiratData?.publicProfileBaseUrl ??
  'https://pfandpirat.com/piraten/';

const AGE_OPTIONS_FALLBACK = ['unter 18', '18-24', '25-34', '35-44', '45-54', '55+'];

const defaultPublicProfile = {
  username: '',
  slug: '',
  display_name: '',
  public_url: '',
  app_url: '',
  enabled: true,
  city: '',
  age_range: '',
  bio: '',
  reason: '',
  show_xp: true,
  show_level: true,
  show_achievements: true,
  show_last_activity: true,
  show_activity_feed: true,
};

function PublicProfileCard({
  form,
  setForm,
  allowedAgeRanges,
  loadingProfile,
  savingProfile,
  publicProfileCompletion,
  publicProfileMissing,
  handleSavePublicProfile,
  handleOpenPublicProfile,
  handleCopyPublicProfileLink,
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const inputStyle = {
    width: '100%',
    padding: '0.85rem 1rem',
    borderRadius: '12px',
    border: '1px solid #d7dce2',
    background: '#fff',
    fontSize: '1rem',
    boxSizing: 'border-box',
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: '110px',
    resize: 'vertical',
    lineHeight: 1.5,
    fontFamily: 'inherit',
  };

  const boxStyle = {
    background: '#fff',
    border: '1px solid #e4e9ef',
    borderRadius: '16px',
    padding: '1rem',
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
  };

  const sectionTitleStyle = {
    margin: '0 0 0.35rem 0',
    color: '#0c3563',
    fontSize: '1.05rem',
    fontWeight: 700,
  };

  const hintStyle = {
    margin: 0,
    color: '#5f6b7a',
    fontSize: '0.92rem',
    lineHeight: 1.45,
  };

  const currentPublicUrl =
    form.public_url ||
    (form.slug ? `${PUBLIC_PROFILE_BASE_URL}${encodeURIComponent(form.slug)}` : '');

  const accordionSummaryParts = [];

  accordionSummaryParts.push(form.enabled ? 'Profil öffentlich aktiv' : 'Profil aktuell privat');

  if ((form.city || '').trim()) {
    accordionSummaryParts.push(`Heimathafen: ${form.city.trim()}`);
  }

  accordionSummaryParts.push(`${publicProfileCompletion}% ausgefüllt`);

  return (
    <div
      style={{
        background: 'linear-gradient(180deg, #fffdf8 0%, #f8fbff 100%)',
        border: '2px solid #f0c96b',
        borderRadius: '18px',
        padding: '1rem',
        margin: '0 0 1.25rem 0',
        boxShadow: '0 6px 20px rgba(0,0,0,0.05)',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: '1rem',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          marginBottom: '1rem',
        }}
      >
        <div style={{ flex: '1 1 380px', minWidth: 0 }}>
          <div
            style={{
              display: 'inline-block',
              fontSize: '0.8rem',
              fontWeight: 700,
              color: '#9a6200',
              background: '#fff3d6',
              borderRadius: '999px',
              padding: '0.3rem 0.7rem',
              marginBottom: '0.65rem',
            }}
          >
            Dein öffentliches Piratenprofil
          </div>

          <h3
            style={{
              margin: '0 0 0.45rem 0',
              color: '#0c3563',
              fontSize: '1.25rem',
              lineHeight: 1.2,
            }}
          >
            Zeig der Crew, wer du bist
          </h3>

          <p
            style={{
              margin: 0,
              color: '#4f5d6c',
              fontSize: '0.97rem',
              lineHeight: 1.5,
            }}
          >
            Hier pflegst du freiwillige Angaben für dein öffentliches Profil.
            Sichtbar werden nur die Dinge, die du wirklich freigibst. Geldsummen,
            Pfandbeträge und Einlösungen bleiben privat.
          </p>
        </div>

        <div
          style={{
            flex: '0 0 190px',
            background: '#ffffff',
            border: '1px solid #e4e9ef',
            borderRadius: '16px',
            padding: '0.9rem',
            minWidth: '190px',
          }}
        >
          <div style={{ fontSize: '0.82rem', color: '#6b7580', marginBottom: '0.35rem' }}>
            Profilstatus
          </div>
          <div
            style={{
              fontSize: '1.45rem',
              fontWeight: 800,
              color: '#0c3563',
              marginBottom: '0.55rem',
            }}
          >
            {publicProfileCompletion}%
          </div>
          <div
            style={{
              width: '100%',
              height: '10px',
              background: '#e7edf4',
              borderRadius: '999px',
              overflow: 'hidden',
              marginBottom: '0.65rem',
            }}
          >
            <div
              style={{
                width: `${publicProfileCompletion}%`,
                height: '100%',
                background: publicProfileCompletion >= 80 ? '#28a745' : '#1e88e5',
                borderRadius: '999px',
                transition: 'width 0.2s ease',
              }}
            />
          </div>
          <div style={{ fontSize: '0.88rem', color: '#65707d', lineHeight: 1.45 }}>
            {publicProfileMissing.length > 0
              ? `Noch offen: ${publicProfileMissing.join(', ')}`
              : 'Dein öffentliches Profil ist gut ausgefüllt.'}
          </div>
        </div>
      </div>

      <div
        style={{
          ...boxStyle,
          marginBottom: '1rem',
          background: '#f7fbff',
          border: '1px solid #d7e8fb',
        }}
      >
        <div style={{ ...sectionTitleStyle, marginBottom: '0.5rem' }}>
          Deine öffentliche Profil-URL
        </div>
        <div
          style={{
            wordBreak: 'break-all',
            fontSize: '0.95rem',
            color: '#0c3563',
            marginBottom: '0.85rem',
          }}
        >
          {currentPublicUrl || 'Wird geladen ...'}
        </div>

        <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleOpenPublicProfile}
            style={{
              padding: '0.7rem 1rem',
              borderRadius: '10px',
              border: 'none',
              background: '#1e88e5',
              color: '#fff',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Öffentliches Profil ansehen
          </button>

          <button
            type="button"
            onClick={handleCopyPublicProfileLink}
            style={{
              padding: '0.7rem 1rem',
              borderRadius: '10px',
              border: '1px solid #c9d3de',
              background: '#fff',
              color: '#0c3563',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Profil-Link kopieren
          </button>
        </div>
      </div>

      <div
        style={{
          ...boxStyle,
          padding: 0,
          overflow: 'hidden',
          marginBottom: '0.25rem',
          border: '1px solid #d9e2ec',
        }}
      >
        <button
          type="button"
          onClick={() => setSettingsOpen((prev) => !prev)}
          aria-expanded={settingsOpen}
          aria-controls="public-profile-accordion-content"
          style={{
            width: '100%',
            border: 'none',
            background: settingsOpen ? '#f7fbff' : '#ffffff',
            padding: '1rem',
            cursor: 'pointer',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                margin: '0 0 0.25rem 0',
                color: '#0c3563',
                fontSize: '1.02rem',
                fontWeight: 800,
                lineHeight: 1.2,
              }}
            >
              Öffentliche Profil-Einstellungen
            </div>

            <div
              style={{
                color: '#5f6b7a',
                fontSize: '0.92rem',
                lineHeight: 1.45,
                wordBreak: 'break-word',
              }}
            >
              {accordionSummaryParts.join(' · ')}
            </div>
          </div>

          <div
            style={{
              flex: '0 0 auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.55rem',
              color: '#0c3563',
              fontWeight: 700,
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
            }}
          >
            <span>{settingsOpen ? 'Zuklappen' : 'Aufklappen'}</span>
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                transform: settingsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
                fontSize: '1.1rem',
                lineHeight: 1,
              }}
            >
              ▼
            </span>
          </div>
        </button>

        {settingsOpen && (
          <div
            id="public-profile-accordion-content"
            style={{
              padding: '1rem',
              borderTop: '1px solid #e4e9ef',
              background: '#fcfdff',
            }}
          >
            {loadingProfile ? (
              <div style={{ padding: '0.25rem 0', color: '#5f6b7a' }}>Profil wird geladen ...</div>
            ) : (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: '1rem',
                    marginBottom: '1rem',
                  }}
                >
                  <div style={boxStyle}>
                    <div style={sectionTitleStyle}>Öffentliches Profil aktivieren</div>
                    <p style={{ ...hintStyle, marginBottom: '0.75rem' }}>
                      Nur wenn diese Option aktiv ist, ist dein Profil öffentlich sichtbar.
                    </p>

                    <label
                      style={{
                        display: 'flex',
                        gap: '0.75rem',
                        alignItems: 'center',
                        fontWeight: 600,
                        color: '#24313f',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!form.enabled}
                        onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                      />
                      Mein Pfandpirat-Profil darf öffentlich angezeigt werden
                    </label>
                  </div>

                  <div style={boxStyle}>
                    <div style={sectionTitleStyle}>Heimathafen</div>
                    <p style={{ ...hintStyle, marginBottom: '0.75rem' }}>
                      Zum Beispiel Dresden, Leipzig oder Hamburg.
                    </p>
                    <input
                      type="text"
                      value={form.city}
                      onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
                      placeholder="Dein Heimathafen"
                      maxLength={80}
                      style={inputStyle}
                    />
                  </div>

                  <div style={boxStyle}>
                    <div style={sectionTitleStyle}>Altersbereich</div>
                    <p style={{ ...hintStyle, marginBottom: '0.75rem' }}>
                      Optional. Besser als ein exaktes Alter.
                    </p>
                    <select
                      value={form.age_range}
                      onChange={(e) => setForm((prev) => ({ ...prev, age_range: e.target.value }))}
                      style={inputStyle}
                    >
                      <option value="">Nicht angeben</option>
                      {(allowedAgeRanges.length ? allowedAgeRanges : AGE_OPTIONS_FALLBACK).map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={boxStyle}>
                    <div style={sectionTitleStyle}>Sichtbarkeit</div>
                    <p style={hintStyle}>
                      Du entscheidest selbst, welche Community-Daten öffentlich erscheinen.
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: '1rem',
                    marginBottom: '1rem',
                  }}
                >
                  {[
                    ['XP öffentlich anzeigen', 'show_xp'],
                    ['Level öffentlich anzeigen', 'show_level'],
                    ['Erfolge öffentlich anzeigen', 'show_achievements'],
                    ['Letzte Aktivität grob anzeigen', 'show_last_activity'],
                    ['Aktivitätsfeed öffentlich anzeigen', 'show_activity_feed'],
                  ].map(([label, key]) => (
                    <label
                      key={key}
                      style={{
                        ...boxStyle,
                        display: 'flex',
                        gap: '0.75rem',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontWeight: 600,
                        color: '#24313f',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!form[key]}
                        onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.checked }))}
                      />
                      {label}
                    </label>
                  ))}
                </div>

                <div style={{ ...boxStyle, marginBottom: '1rem' }}>
                  <div style={sectionTitleStyle}>Über dich</div>
                  <p style={{ ...hintStyle, marginBottom: '0.75rem' }}>
                    Erzähl der Crew ein bisschen über dich.
                  </p>
                  <textarea
                    value={form.bio}
                    onChange={(e) => setForm((prev) => ({ ...prev, bio: e.target.value }))}
                    placeholder="Erzähl kurz etwas über dich ..."
                    maxLength={600}
                    style={textareaStyle}
                  />
                </div>

                <div style={{ ...boxStyle, marginBottom: '1rem' }}>
                  <div style={sectionTitleStyle}>Warum nutzt du Pfandpirat?</div>
                  <p style={{ ...hintStyle, marginBottom: '0.75rem' }}>
                    Was motiviert dich an der App oder an der Idee dahinter?
                  </p>
                  <textarea
                    value={form.reason}
                    onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
                    placeholder="Warum bist du bei Pfandpirat dabei?"
                    maxLength={600}
                    style={textareaStyle}
                  />
                </div>

                <div
                  style={{
                    background: '#fff6e7',
                    border: '1px solid #f0cf84',
                    borderRadius: '14px',
                    padding: '0.9rem 1rem',
                    color: '#805300',
                    fontSize: '0.94rem',
                    lineHeight: 1.5,
                    marginBottom: '1rem',
                  }}
                >
                  Wichtig: Pfandbeträge, Schatzsummen, Einlösungen und andere Geldwerte
                  bleiben immer privat und werden in deinem öffentlichen Profil bewusst
                  nicht angezeigt.
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handleSavePublicProfile}
                    disabled={savingProfile}
                    style={{
                      padding: '0.8rem 1.15rem',
                      borderRadius: '10px',
                      border: 'none',
                      background: savingProfile ? '#8dbbe8' : '#ff9800',
                      color: '#fff',
                      fontWeight: 700,
                      cursor: savingProfile ? 'default' : 'pointer',
                    }}
                  >
                    {savingProfile ? 'Profil wird gespeichert ...' : 'Öffentliches Profil speichern'}
                  </button>

                  <button
                    type="button"
                    onClick={handleOpenPublicProfile}
                    style={{
                      padding: '0.8rem 1.15rem',
                      borderRadius: '10px',
                      border: '1px solid #c9d3de',
                      background: '#fff',
                      color: '#0c3563',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Öffentliches Profil ansehen
                  </button>

                  <button
                    type="button"
                    onClick={handleCopyPublicProfileLink}
                    style={{
                      padding: '0.8rem 1.15rem',
                      borderRadius: '10px',
                      border: '1px solid #c9d3de',
                      background: '#fff',
                      color: '#0c3563',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Profil-Link kopieren
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileTabButton({ id, label, isActive, onClick }) {
  return (
    <button
      type="button"
      className={`pfp-profile-tab ${isActive ? 'is-active' : ''}`}
      onClick={() => onClick(id)}
    >
      {label}
    </button>
  );
}

function ProfileOverviewCard({ title, subtitle, actions, children }) {
  return (
    <section className="pfp-profile-card">
      <div className="pfp-profile-card__header">
        <div>
          <h3 className="pfp-profile-card__title">{title}</h3>
          {subtitle ? <p className="pfp-profile-card__subtitle">{subtitle}</p> : null}
        </div>
        {actions ? <div className="pfp-profile-card__actions">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

function ProfileHero({
  user,
  selectedAvatar,
  avatarData,
  avatarLoading,
  onOpenAvatarPicker,
  totalPfand,
  availablePfand,
  xp,
  level,
  rankTitle,
  progressLabel,
  progressPercent,
  onRedeemAvailable,
  receiptRedemptionsEnabled,
  onOpenProgress,
  onOpenInventory,
}) {
  const availableAmount = Number(availablePfand || 0);
  const primaryActionDisabled = !receiptRedemptionsEnabled && availableAmount <= 0;
  const primaryActionLabel = receiptRedemptionsEnabled
    ? (availableAmount > 0 ? `Pfand bestätigen (${availableAmount.toFixed(2)} €)` : 'Pfand bestätigen')
    : (availableAmount > 0 ? `Jetzt einlösen (${availableAmount.toFixed(2)} €)` : 'Nichts verfügbar');

  return (
    <section className="pfp-profile-hero">
      <div className="pfp-profile-hero__top">
        <div className="pfp-profile-hero__identity">
          <PlayerAvatarImage
            className="pfp-profile-hero__avatar"
            avatarData={avatarData}
            alt={selectedAvatar?.title || user?.name || 'Dein Avatar'}
          />

          <div className="pfp-profile-hero__meta">
            <div className="pfp-profile-hero__eyebrow">Dein App-Profil</div>
            <h2 className="pfp-profile-hero__name">{user?.name || 'Unbekannt'}</h2>
            <div className="pfp-profile-hero__rank">{rankTitle} · Level {level}</div>
          </div>
        </div>

        <button
          type="button"
          className="pfp-profile-hero__avatar-button"
          onClick={onOpenAvatarPicker}
          disabled={avatarLoading}
        >
          {avatarLoading ? 'Avatare laden ...' : 'Avatar ändern'}
        </button>
      </div>

      <div className="pfp-profile-hero__main-grid">
        <div>
          <div className="pfp-profile-hero__label">Dein gesamter Schatz</div>
          <div className="pfp-profile-hero__amount">{Number(totalPfand || 0).toFixed(2)} €</div>
          <div className="pfp-profile-hero__subamount">{Number(availablePfand || 0).toFixed(2)} € aktuell verfügbar</div>
        </div>

        <div className="pfp-profile-hero__quick-actions">
          <button
            type="button"
            className="pfp-profile-hero__primary-action"
            onClick={() => onRedeemAvailable?.()}
            disabled={primaryActionDisabled}
          >
            {primaryActionLabel}
          </button>
          <button type="button" className="pfp-profile-hero__secondary-action" onClick={onOpenInventory}>
            Inventar
          </button>
          <button type="button" className="pfp-profile-hero__secondary-action" onClick={onOpenProgress}>
            Erfolge & XP
          </button>
        </div>
      </div>

      <div className="pfp-profile-hero__progress-block">
        <div className="pfp-profile-hero__progress-meta">
          <span>Gesamte XP</span>
          <span>{xp}</span>
        </div>
        <div className="pfp-profile-hero__progress-track">
          <span style={{ width: `${Math.max(0, Math.min(100, progressPercent))}%` }} />
        </div>
        <div className="pfp-profile-hero__progress-subline">{progressLabel}</div>
      </div>
    </section>
  );
}

function CompactAchievementPreview({ achievements, onOpenProgress }) {
  if (!achievements.length) {
    return (
      <div className="pfp-profile-empty-state">
        Noch keine Erfolge sichtbar. Sammle weiter Pfand und Schritte.
      </div>
    );
  }

  return (
    <>
      <div className="pfp-profile-chip-list">
        {achievements.slice(0, 6).map((achievement) => (
          <div key={achievement.key} className="pfp-profile-chip">
            <span>{achievement.badge_icon || '🏅'}</span>
            <span>{achievement.title}</span>
          </div>
        ))}
      </div>

      {achievements.length > 6 ? (
        <div className="pfp-profile-inline-link-row">
          <button type="button" className="pfp-profile-inline-link" onClick={onOpenProgress}>
            Alle {achievements.length} Erfolge ansehen
          </button>
        </div>
      ) : null}
    </>
  );
}

function MoneySummaryGrid({ availablePfand, totalPfand, redeemedPfand, publicProfileCompletion }) {
  return (
    <div className="pfp-profile-stat-grid">
      <div className="pfp-profile-stat-card">
        <div className="pfp-profile-stat-card__label">Verfügbar</div>
        <div className="pfp-profile-stat-card__value is-success">{Number(availablePfand || 0).toFixed(2)} €</div>
        <div className="pfp-profile-stat-card__hint">sofort einlösbar</div>
      </div>

      <div className="pfp-profile-stat-card">
        <div className="pfp-profile-stat-card__label">Gesamt</div>
        <div className="pfp-profile-stat-card__value">{Number(totalPfand || 0).toFixed(2)} €</div>
        <div className="pfp-profile-stat-card__hint">seit App-Start gesammelt</div>
      </div>

      <div className="pfp-profile-stat-card">
        <div className="pfp-profile-stat-card__label">Bereits eingelöst</div>
        <div className="pfp-profile-stat-card__value is-info">{Number(redeemedPfand || 0).toFixed(2)} €</div>
        <div className="pfp-profile-stat-card__hint">schon ausgezahlt</div>
      </div>

      <div className="pfp-profile-stat-card">
        <div className="pfp-profile-stat-card__label">Profilstatus</div>
        <div className="pfp-profile-stat-card__value">{publicProfileCompletion}%</div>
        <div className="pfp-profile-stat-card__hint">öffentliches Profil</div>
      </div>
    </div>
  );
}


export function Profile({ user, entries, onUpdate, onRedeem, applyReceiptRedemptionResult }) {
  const [loading, setLoading] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptInitialMode, setReceiptInitialMode] = useState('match_existing');
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [publicProfileForm, setPublicProfileForm] = useState(defaultPublicProfile);
  const [allowedAgeRanges, setAllowedAgeRanges] = useState([]);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [pushStatus, setPushStatus] = useState({
    enabled: Boolean(getConfiguredOneSignalAppId()),
    supported: null,
    permission: false,
    permissionState: typeof window !== 'undefined' && typeof window.Notification !== 'undefined'
      ? window.Notification.permission
      : 'default',
    optedIn: false,
    subscriptionId: '',
    isIos: false,
    isStandalone: false,
    needsHomeScreenInstall: false,
  });
  const [pushUpdating, setPushUpdating] = useState(false);
  const [pushTestSending, setPushTestSending] = useState(false);
  const [pushTestResult, setPushTestResult] = useState('');
  const [receiptBetaUpdating, setReceiptBetaUpdating] = useState(false);
  const [korkVoiceEnabled, setKorkVoiceEnabledState] = useState(() => {
    try {
      return isKorkVoiceEnabled();
    } catch {
      return true;
    }
  });
  const [challengeFocusEnabled, setChallengeFocusEnabledState] = useState(() => {
    try {
      return isChallengeFocusEnabled();
    } catch {
      return false;
    }
  });
  const firstInputRef = useRef(null);
  const achievements = useAchievements();

  const initialTab = (() => {
    try {
      const stored = window.localStorage.getItem('pfandpirat_profile_tab');
      if (stored && ['overview', 'progress', 'friends', 'settings'].includes(stored)) {
        return stored;
      }
    } catch {}
    return 'overview';
  })();

  const [activeTab, setActiveTab] = useState(initialTab);

  const { giveXP } = useXpBonus();
  const { show } = useToast();
  const sessionContext = useOptionalAppSession();
  const featureFlags = sessionContext?.session?.features || {};
  const receiptRedemptionsAvailable = featureFlags.receipt_redemptions_available === true;
  const receiptBetaRequired = featureFlags.receipt_redemptions_beta_required !== false;
  const receiptBetaEnabled = featureFlags.receipt_redemptions_beta_enabled === true
    || (receiptRedemptionsAvailable && !receiptBetaRequired);
  const receiptRedemptionsEnabled = featureFlags.receipt_redemptions === true;
  const {
    queuePresentation,
    queuePresentations,
    xp = 0,
    level = 0,
    rank = null,
    progress = null,
  } = useGamification();
  const {
    avatarData,
    selectedAvatar,
    avatars,
    loading: avatarLoading,
    saving: avatarSaving,
    selectAvatar,
  } = useAvatarWardrobe({ enabled: !!user?.id });

  const {
    visits: recentProfileVisits,
    loading: visitsLoading,
    error: visitsError,
    enabled: visitsEnabled,
  } = useProfileVisits({ enabled: !!user?.id, limit: 6 });

  // ----------------- Totals -----------------
  const [totals, setTotals] = useState(null);

  async function loadTotals() {
    try {
      const res = await fetch('/wp-json/pfandpirat/v1/totals', {
        credentials: 'include',
        headers: { 'X-WP-Nonce': NONCE },
      });
      if (!res.ok) throw new Error('Totals API error');
      const data = await res.json();
      setTotals(data);
    } catch (e) {
      console.warn('Totals konnten nicht geladen werden:', e);
      setTotals(previous => previous || { total_collected: 0, total_redeemed: 0, available: 0 });
    }
  }

  useEffect(() => {
    loadTotals();
  }, []);

  useEffect(() => {
    if (!user?.id || !getConfiguredOneSignalAppId()) {
      return undefined;
    }

    let alive = true;

    getOneSignalAppStatus().then((status) => {
      if (alive) {
        setPushStatus(status);
      }
    });

    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    try {
      window.localStorage.setItem('pfandpirat_profile_tab', activeTab);
    } catch {}
  }, [activeTab]);

  // ----------------- Öffentliches Profil -----------------
  function handleKorkVoiceToggle(enabled) {
    setKorkVoiceEnabledState(enabled);
    try {
      setKorkVoiceEnabled(enabled);
    } catch {}
    show(enabled ? "Käpt'n Korks Stimme ist aktiviert." : "Käpt'n Korks Stimme ist deaktiviert.", 'success');
  }

  function handleChallengeFocusToggle(enabled) {
    setChallengeFocusEnabledState(enabled);
    try {
      setChallengeFocusEnabled(enabled);
    } catch {}
    show(
      enabled
        ? 'Challenge-Fokus ist aktiv. Punkte laufen weiter, Popups bleiben ruhig.'
        : 'Challenge-Fokus ist deaktiviert. Belohnungsanzeigen sind wieder sichtbar.',
      'success'
    );
  }

  async function loadPublicProfileSettings() {
    setLoadingProfile(true);

    try {
      const res = await fetch(PUBLIC_PROFILE_SETTINGS_API, {
        credentials: 'include',
        headers: {
          'X-WP-Nonce': NONCE,
        },
      });

      if (!res.ok) {
        throw new Error('Public profile settings konnten nicht geladen werden.');
      }

      const data = await res.json();
      const profile = data?.profile ?? {};
      const allowed = Array.isArray(data?.allowed_age_ranges) ? data.allowed_age_ranges : [];

      setAllowedAgeRanges(allowed);

      setPublicProfileForm({
        ...defaultPublicProfile,
        ...profile,
        enabled: !!profile.enabled,
        show_xp: profile.show_xp !== false,
        show_level: profile.show_level !== false,
        show_achievements: profile.show_achievements !== false,
        show_last_activity: profile.show_last_activity ?? true,
        show_activity_feed: profile.show_activity_feed ?? profile.show_last_activity ?? true,
      });
    } catch (error) {
      console.error(error);
      show('Die Einstellungen für dein öffentliches Profil konnten nicht geladen werden.', 'error');
    } finally {
      setLoadingProfile(false);
    }
  }

  useEffect(() => {
    loadPublicProfileSettings();
  }, []);

  const publicProfileCompletion = useMemo(() => {
    let score = 0;

    if (publicProfileForm.enabled) score += 20;
    if ((publicProfileForm.city || '').trim()) score += 20;
    if ((publicProfileForm.age_range || '').trim()) score += 10;
    if ((publicProfileForm.bio || '').trim()) score += 25;
    if ((publicProfileForm.reason || '').trim()) score += 25;

    return score;
  }, [publicProfileForm]);

  const publicProfileMissing = useMemo(() => {
    const missing = [];

    if (!publicProfileForm.enabled) missing.push('Profil aktivieren');
    if (!(publicProfileForm.city || '').trim()) missing.push('Heimathafen angeben');
    if (!(publicProfileForm.bio || '').trim()) missing.push('Über-mich-Text ergänzen');
    if (!(publicProfileForm.reason || '').trim()) missing.push('Motivation ergänzen');

    return missing;
  }, [publicProfileForm]);

  async function handleSavePublicProfile() {
    setSavingProfile(true);

    try {
      const payload = {
        enabled: !!publicProfileForm.enabled,
        city: publicProfileForm.city || '',
        age_range: publicProfileForm.age_range || '',
        bio: publicProfileForm.bio || '',
        reason: publicProfileForm.reason || '',
        show_xp: !!publicProfileForm.show_xp,
        show_level: !!publicProfileForm.show_level,
        show_achievements: !!publicProfileForm.show_achievements,
        show_last_activity: !!publicProfileForm.show_last_activity,
        show_activity_feed: !!publicProfileForm.show_activity_feed,
      };

      const res = await fetch(PUBLIC_PROFILE_SETTINGS_API, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': NONCE,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.success) {
        throw new Error(data?.message || 'Speichern fehlgeschlagen.');
      }

      const profile = data?.profile ?? {};

      setPublicProfileForm((prev) => ({
        ...prev,
        ...profile,
        enabled: !!profile.enabled,
        show_xp: profile.show_xp !== false,
        show_level: profile.show_level !== false,
        show_achievements: profile.show_achievements !== false,
        show_last_activity: profile.show_last_activity ?? true,
        show_activity_feed: profile.show_activity_feed ?? profile.show_last_activity ?? true,
      }));

      show('Dein öffentliches Profil wurde gespeichert.', 'success');
    } catch (error) {
      console.error(error);
      show('Dein öffentliches Profil konnte nicht gespeichert werden.', 'error');
    } finally {
      setSavingProfile(false);
    }
  }

  function handleOpenPublicProfile() {
    const url =
      publicProfileForm.public_url ||
      (publicProfileForm.slug ? `${PUBLIC_PROFILE_BASE_URL}${encodeURIComponent(publicProfileForm.slug)}` : '');

    if (!url) {
      show('Die öffentliche Profil-URL ist noch nicht verfügbar.', 'error');
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async function handleCopyPublicProfileLink() {
    const url =
      publicProfileForm.public_url ||
      (publicProfileForm.slug ? `${PUBLIC_PROFILE_BASE_URL}${encodeURIComponent(publicProfileForm.slug)}` : '');

    if (!url) {
      show('Die öffentliche Profil-URL ist noch nicht verfügbar.', 'error');
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const helper = document.createElement('textarea');
        helper.value = url;
        helper.style.position = 'fixed';
        helper.style.opacity = '0';
        document.body.appendChild(helper);
        helper.focus();
        helper.select();
        document.execCommand('copy');
        document.body.removeChild(helper);
      }

      show('Profil-Link wurde in die Zwischenablage kopiert.', 'success');
    } catch (error) {
      console.error(error);
      show('Der Profil-Link konnte nicht kopiert werden.', 'error');
    }
  }

  // ----------------- Helfer -----------------
  function getChestType(amount) {
    const a = Number(amount || 0);
    if (a >= 25) return 'diamond';
    if (a >= 15) return 'gold';
    if (a >= 5) return 'silver';
    return 'bronze';
  }

  const formatAmount = (n) => Number(n || 0).toFixed(2);

  function buildChestPresentation(type, amount) {
    const defaults = window?.pfandpiratData?.achievementMediaDefaults || {};
    const imageMap = {
      bronze: defaults.chest_bronze_image || '',
      silver: defaults.chest_silver_image || '',
      gold: defaults.chest_gold_image || '',
      diamond: defaults.chest_diamond_image || '',
    };
    const labelMap = {
      bronze: 'Bronze Schatztruhe',
      silver: 'Silber Schatztruhe',
      gold: 'Goldene Schatztruhe',
      diamond: 'Diamant Schatztruhe',
    };
    const rarityMap = {
      bronze: 'common',
      silver: 'rare',
      gold: 'epic',
      diamond: 'legendary',
    };
    const iconMap = {
      bronze: '🧰',
      silver: '🥈',
      gold: '🥇',
      diamond: '💎',
    };

    return {
      key: `chest_${type}_${Date.now()}`,
      title: labelMap[type] || 'Schatztruhe',
      popup_title: labelMap[type] || 'Schatztruhe',
      popup_text: `Du hast ${formatAmount(amount)} € Pfand eingelöst und eine ${labelMap[type] || 'Schatztruhe'} geöffnet!`,
      badge_image_url: imageMap[type] || '',
      sound_url: defaults.xp_gain_sound || '',
      badge_icon: iconMap[type] || '🎁',
      rarity: rarityMap[type] || 'common',
      presentation_type: 'chest',
    };
  }

  // Gesamtsumme (bevorzugt aus Totals, sonst Fallback über entries)
  const totalPfand = useMemo(() => {
    if (totals && typeof totals === 'object') {
      return Number(totals.total_collected || 0);
    }
    const list = Array.isArray(entries) ? entries : [];
    return list.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  }, [totals, entries]);

  const availablePfand = useMemo(() => {
    if (totals && typeof totals === 'object') {
      return Number(totals.available || 0);
    }

    const list = Array.isArray(entries) ? entries : [];
    const totalRedeemed = list
      .filter((entry) => entry.status === 'redeemed')
      .reduce((sum, entry) => sum + (parseFloat(entry.amount) || 0), 0);

    return Math.max(0, Number(totalPfand || 0) - totalRedeemed);
  }, [totals, entries, totalPfand]);

  const redeemedPfand = useMemo(() => {
    if (totals && typeof totals === 'object') {
      return Number(totals.total_redeemed || 0);
    }

    const list = Array.isArray(entries) ? entries : [];
    return list
      .filter((entry) => entry.status === 'redeemed')
      .reduce((sum, entry) => sum + (parseFloat(entry.amount) || 0), 0);
  }, [totals, entries]);


  // ----------------- Einlösen -----------------
  async function redeemAllMyOpenFallbackBatch(myOpenIds, batchSize = 10) {
    for (let i = 0; i < myOpenIds.length; i += batchSize) {
      const slice = myOpenIds.slice(i, i + batchSize);
      await Promise.all(
        slice.map((id) =>
          fetch(`/wp-json/pfandpirat/v1/entries/${id}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': NONCE },
            body: JSON.stringify({ status: 'redeemed' }),
          })
        )
      );
    }
  }

  async function redeemAllMyOpen() {
    try {
      const bulkRes = await fetch('/wp-json/pfandpirat/v1/entries/redeem-all', {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-WP-Nonce': NONCE },
      });

      if (bulkRes.ok) {
        const data = await bulkRes.json().catch(() => ({}));
        if (data && data.totals) setTotals(data.totals);
        return {
          ok: (data?.updated ?? 0) > 0,
          totals: data?.totals,
          sum: Number(data?.sum || 0),
          rewardPresentation: data?.reward_presentation || null,
          presentations: Array.isArray(data?.presentations) ? data.presentations : [],
        };
      }

      const myOpenIds = (Array.isArray(entries) ? entries : [])
        .filter((e) => e.status !== 'redeemed' && Number(e.user_id) === Number(user?.id))
        .map((e) => e.id);

      if (!myOpenIds.length) return { ok: false };

      await redeemAllMyOpenFallbackBatch(myOpenIds, 10);
      return { ok: true };
    } catch {
      return { ok: false };
    }
  }

  const handleLegacyRedeem = async (amount) => {
    setLoading(true);
    const chestType = getChestType(amount);

    try {
      if (typeof onRedeem === 'function') {
        onRedeem(amount, chestType);
      }

      const { ok, sum, rewardPresentation, presentations = [] } = await redeemAllMyOpen();

      if (!ok) {
        show('Keine eigenen offenen Einträge gefunden oder Einlösen fehlgeschlagen.', 'error');
        setLoading(false);
        return;
      }

      await loadTotals();
      await giveXP(XP_EVENTS.REDEEM_PFAND);

      const redeemedAmount = Number(sum || amount || 0);
      const resolvedPresentation = rewardPresentation || buildChestPresentation(chestType, redeemedAmount);
      const queuedFromApi = queuePresentations(presentations);

      markChestAsOpened(chestType);
      if (!queuedFromApi.length) {
        queuePresentation(resolvedPresentation);
      }

      if (typeof onUpdate === 'function') {
        onUpdate();
      }
    } catch (e) {
      show('Einlösen ist leider fehlgeschlagen.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = (amount = availablePfand) => {
    const redeemableAmount = Number(amount || 0);

    if (receiptRedemptionsEnabled) {
      setReceiptInitialMode(redeemableAmount > 0 ? 'match_existing' : 'create_redeemed_entry');
      setReceiptModalOpen(true);
      return;
    }

    if (redeemableAmount > 0) {
      handleLegacyRedeem(redeemableAmount);
    }
  };

  const handleReceiptRedemptionSuccess = async (result) => {
    const redeemedAmount = getReceiptRedeemedAmount(result);
    const applied = classifyReceiptRedemption(result) === 'success';

    if (typeof applyReceiptRedemptionResult === 'function') {
      applyReceiptRedemptionResult(result);
    } else if (result?.session_patch && sessionContext?.mergeSessionPatch) {
      sessionContext.mergeSessionPatch(result.session_patch);
    }
    if (result?.session_patch?.totals) {
      setTotals(result.session_patch.totals);
    }

    if (!applied || redeemedAmount <= 0) {
      show(getReceiptRedemptionMessage(result), 'info');
      if (typeof onUpdate === 'function') {
        Promise.resolve(onUpdate()).catch(() => {});
      }
      return;
    }

    const chestType = getChestType(redeemedAmount);

    setReceiptModalOpen(false);
    show(getReceiptRedemptionMessage(result), 'success');

    try {
      if (typeof onRedeem === 'function') {
        onRedeem(redeemedAmount, chestType);
      }

      if (!result?.client_duplicate_replay) {
        markChestAsOpened(chestType);
        queuePresentation(buildChestPresentation(chestType, redeemedAmount));
      }

      const refreshes = [loadTotals()];
      if (!result?.client_duplicate_replay) {
        refreshes.push(giveXP(XP_EVENTS.REDEEM_PFAND));
      }
      if (typeof onUpdate === 'function') {
        refreshes.push(Promise.resolve(onUpdate()));
      }
      const refreshResults = await Promise.allSettled(refreshes);
      if (refreshResults.some(item => item.status === 'rejected')) {
        console.warn('[Pfandpirat] Bon ist erfolgreich eingelöst; eine optionale Ansicht konnte nicht aktualisiert werden.');
      }
    } catch (e) {
      console.warn('[Pfandpirat] Bon ist erfolgreich eingelöst; optionale Folgeaktion fehlgeschlagen.', e);
    }
  };


  // ----------------- Accessibility -----------------
  const {
    referralData,
    stats: referralStats,
    loading: referralLoading,
    error: referralError,
    reload: reloadReferrals,
    trackShare,
  } = useReferrals();

  const [showNewProject, setShowNewProject] = useState(false);

  useEffect(() => {
    if (!showNewProject) return;
    firstInputRef.current?.focus();
    const onEsc = (e) => e.key === 'Escape' && setShowNewProject(false);
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [showNewProject]);

  async function handleSelectAvatar(avatarId) {
    try {
      await selectAvatar(avatarId);
      show('Dein Avatar wurde gespeichert.', 'success');
      setAvatarPickerOpen(false);
    } catch (error) {
      console.error(error);
      show(error?.message || 'Dein Avatar konnte nicht gespeichert werden.', 'error');
    }
  }

  async function handlePushAlarmToggle(enabled) {
    if (enabled && pushStatus.needsHomeScreenInstall) {
      show('Auf dem iPhone bitte erst zum Home-Bildschirm hinzufügen und von dort öffnen.', 'error');
      return;
    }

    setPushUpdating(true);
    setPushTestResult('');

    try {
      const nextStatus = await setOneSignalPushSubscriptionEnabled(enabled);
      setPushStatus(nextStatus);

      if (enabled && nextStatus?.optedIn && nextStatus?.subscriptionId) {
        show('Pfand-Alarm ist aktiviert.', 'success');
      } else if (!enabled) {
        show('Pfand-Alarm ist deaktiviert.', 'success');
      } else if (nextStatus?.permissionState === 'denied') {
        show('Benachrichtigungen sind im Browser blockiert.', 'error');
      } else {
        show('Pfand-Alarm konnte noch nicht vollständig aktiviert werden.', 'error');
      }
    } catch (error) {
      console.error(error);
      show('Pfand-Alarm konnte nicht aktualisiert werden.', 'error');
    } finally {
      setPushUpdating(false);
    }
  }

  async function handleReceiptBetaToggle(enabled) {
    if (!receiptRedemptionsAvailable) {
      show('Die Pfandbon-Beta ist aktuell noch nicht verfügbar.', 'error');
      return;
    }

    setReceiptBetaUpdating(true);

    try {
      const payload = await updateReceiptRedemptionBeta(enabled);
      sessionContext?.mergeSessionPatch?.({
        features: {
          receipt_redemptions: payload?.receipt_redemptions === true,
          receipt_redemptions_available: payload?.receipt_redemptions_available === true,
          receipt_redemptions_beta_enabled: payload?.receipt_redemptions_beta_enabled === true,
          receipt_redemptions_beta_required: payload?.receipt_redemptions_beta_required !== false,
        },
      });

      if (!payload?.receipt_redemptions) {
        setReceiptModalOpen(false);
      }

      show(
        enabled
          ? 'Du bist jetzt im Pfandbon-Beta-Test. Beim Bestätigen kannst du Bons scannen.'
          : 'Pfandbon-Beta ist deaktiviert. Du nutzt wieder die bisherige Einlösung.',
        'success'
      );
    } catch (error) {
      console.error(error);
      show('Der Beta-Status konnte nicht gespeichert werden.', 'error');
    } finally {
      setReceiptBetaUpdating(false);
    }
  }

  const resolvedRankTitle = rank?.title || (level >= 12 ? 'Admiral' : level >= 8 ? 'Kapitän' : level >= 5 ? 'Steuermann' : level >= 3 ? 'Deckhelfer' : 'Matrose');
  async function handleSendPushAlarmTest() {
    setPushTestSending(true);
    setPushTestResult('');

    try {
      const currentStatus = await getOneSignalAppStatus();
      setPushStatus(currentStatus);

      if (!currentStatus?.optedIn || !currentStatus?.subscriptionId) {
        setPushTestResult('Dieses Gerät ist noch nicht für Push angemeldet.');
        show('Dieses Gerät ist noch nicht für Push angemeldet.', 'error');
        return;
      }

      const res = await fetch(ONESIGNAL_TEST_PUSH_API, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': NONCE,
        },
        body: JSON.stringify({
          subscription_id: currentStatus.subscriptionId,
        }),
      });

      const payload = await res.json().catch(() => null);
      const result = payload?.data?.result || payload;

      if (!res.ok) {
        const reason = result?.reason || payload?.message || 'unbekannt';
        setPushTestResult(`Test fehlgeschlagen: ${reason}`);
        show('Der Test-Push konnte nicht zugestellt werden.', 'error');
        return;
      }

      const recipients = Number(result?.recipients || 0);
      const tail = result?.subscription_tail || currentStatus.subscriptionId.slice(-8);
      setPushTestResult(
        recipients > 0
          ? `Test gesendet an dieses Gerät (${tail}), Empfänger: ${recipients}.`
          : `Test an OneSignal übergeben für dieses Gerät (${tail}).`
      );
      show('Test-Push wurde an dieses Gerät gesendet.', 'success');
    } catch (error) {
      console.error(error);
      setPushTestResult('Test fehlgeschlagen. Bitte später noch einmal versuchen.');
      show('Der Test-Push konnte nicht gesendet werden.', 'error');
    } finally {
      setPushTestSending(false);
    }
  }

  const progressPercent = Number(progress?.progress_percent || 0);
  const progressLabel = progress
    ? `${progress.progress_in_level || 0} / ${progress.progress_needed || 0} XP in diesem Level · noch ${progress.xp_to_next_level || 0} XP bis zum nächsten Level`
    : `Sammle weiter XP, um dein nächstes Level zu erreichen.`;

  const pushAlarmActive = !!(pushStatus.optedIn && pushStatus.subscriptionId);
  const pushNeedsIosInstall = !!pushStatus.needsHomeScreenInstall;
  const pushToggleDisabled = pushUpdating || pushStatus.supported === false || pushStatus.permissionState === 'denied';
  const pushAlarmHint = pushStatus.permissionState === 'denied'
    ? 'Benachrichtigungen sind im Browser blockiert. Bitte in den App-Berechtigungen freigeben.'
    : pushNeedsIosInstall
    ? 'Auf dem iPhone muss Pfandpirat zuerst zum Home-Bildschirm hinzugefügt und von dort geöffnet werden.'
    : pushStatus.isIos && pushStatus.isStandalone && pushStatus.supported === false
    ? 'Dieses iPhone kann Web-Push noch nicht starten. Bitte iOS aktualisieren und die App vom Home-Bildschirm öffnen.'
    : pushStatus.supported === false
    ? 'Benachrichtigungen werden auf diesem Gerät oder in diesem Browser nicht unterstützt.'
    : pushAlarmActive
    ? 'Du bekommst eine Push-Nachricht, sobald jemand Pfand auf der Karte markiert.'
    : 'Aktiviert Push-Nachrichten für neue Pfandspenden auf der Karte.';

  return (
    <div className="pfp-profile-page">
      <ReceiptRedemptionModal
        isOpen={receiptRedemptionsEnabled && receiptModalOpen}
        expectedAmount={availablePfand}
        initialMode={receiptInitialMode}
        title="Pfand mit Bon bestätigen"
        onClose={() => setReceiptModalOpen(false)}
        onSuccess={handleReceiptRedemptionSuccess}
      />
      {loading && <Spinner text="Pfand wird eingelöst..." />}

      <ProfileHero
        user={user}
        selectedAvatar={selectedAvatar}
        avatarData={avatarData}
        avatarLoading={avatarLoading}
        onOpenAvatarPicker={() => setAvatarPickerOpen(true)}
        totalPfand={totalPfand}
        availablePfand={availablePfand}
        xp={xp}
        level={level}
        rankTitle={resolvedRankTitle}
        progressLabel={progressLabel}
        progressPercent={progressPercent}
        receiptRedemptionsEnabled={receiptRedemptionsEnabled}
        onRedeemAvailable={() => handleRedeem(availablePfand)}
        onOpenProgress={() => setActiveTab('progress')}
        onOpenInventory={() => {
          window.location.hash = '#/inventar';
        }}
      />

      <div className="pfp-profile-tabs" role="tablist" aria-label="Profilbereiche">
        <ProfileTabButton id="overview" label="Übersicht" isActive={activeTab === 'overview'} onClick={setActiveTab} />
        <ProfileTabButton id="progress" label="Erfolge & XP" isActive={activeTab === 'progress'} onClick={setActiveTab} />
        <ProfileTabButton id="friends" label="Freunde" isActive={activeTab === 'friends'} onClick={setActiveTab} />
        <ProfileTabButton id="settings" label="Profil & Einstellungen" isActive={activeTab === 'settings'} onClick={setActiveTab} />
      </div>

      {activeTab === 'overview' && (
        <div className="pfp-profile-tab-panel">
          <ProfileOverviewCard
            title="Dein Fokusbereich"
            subtitle="Ganz oben nur das, was für den Nutzer zuerst wichtig ist: Schatz, verfügbarer Pfand, Level und Fortschritt."
          >
            <MoneySummaryGrid
              availablePfand={availablePfand}
              totalPfand={totalPfand}
              redeemedPfand={redeemedPfand}
              publicProfileCompletion={publicProfileCompletion}
            />
          </ProfileOverviewCard>

          <CollectionSummaryCard />

          <ProfileOverviewCard
            title="Zuletzt freigeschaltet"
            subtitle="Erfolge bleiben sichtbar, aber nicht mehr als lange Leiste mitten im Profil."
            actions={
              <button type="button" className="pfp-profile-card__link-button" onClick={() => setActiveTab('progress')}>
                Zu Erfolge & XP
              </button>
            }
          >
            <CompactAchievementPreview achievements={achievements} onOpenProgress={() => setActiveTab('progress')} />
          </ProfileOverviewCard>

          <ProfileOverviewCard
            title="Pfandschatz & Einlösung"
            subtitle="Die Finanzwerte bleiben wichtig, sitzen aber sauber in einer separaten Karte statt als langer Block mitten im Profil."
          >
            <PfandOverview totals={totals} entries={entries} onRedeem={handleRedeem} />
          </ProfileOverviewCard>

          <details className="pfp-profile-details-card">
            <summary>Weitere Langzeitwerte anzeigen</summary>
            <div className="pfp-profile-details-card__content">
              <TotalPfandBox total={parseFloat(totalPfand)} />
              <TreasureCollection />
            </div>
          </details>

          <div className="pfp-profile-inventory-cta">
            <Link to="/inventar" className="pfp-profile-inventory-cta__button">
              Zu deinem Inventar →
            </Link>
          </div>
        </div>
      )}

      {activeTab === 'progress' && (
        <div className="pfp-profile-tab-panel">
          <ProfileOverviewCard
            title="Erfolge & XP"
            subtitle="Hier liegt alles, was motiviert: Fortschritt, Rang, Erfolge und Schatztruhen."
          >
            <ProfileGamification />
            <ProfileAchievements />
          </ProfileOverviewCard>

          <ProfileOverviewCard
            title="Schatztruhen-Sammlung"
            subtitle="Dieser Bereich ist weiter da, aber nicht mehr direkt im ersten sichtbaren Bildschirmblock."
          >
            <TreasureCollection />
          </ProfileOverviewCard>

        </div>
      )}

      {activeTab === 'friends' && (
        <div className="pfp-profile-tab-panel">
          <ReferralCard
            user={user}
            referralData={referralData}
            stats={referralStats}
            loading={referralLoading}
            error={referralError}
            onTrackShare={trackShare}
            onReload={reloadReferrals}
          />

          <ProfileOverviewCard
            title="Community"
          >
            <ProfileVisitsCard
              visits={recentProfileVisits}
              loading={visitsLoading}
              error={visitsError}
              enabled={visitsEnabled}
            />
          </ProfileOverviewCard>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="pfp-profile-tab-panel">
          <PublicProfileCard
            form={publicProfileForm}
            setForm={setPublicProfileForm}
            allowedAgeRanges={allowedAgeRanges}
            loadingProfile={loadingProfile}
            savingProfile={savingProfile}
            publicProfileCompletion={publicProfileCompletion}
            publicProfileMissing={publicProfileMissing}
            handleSavePublicProfile={handleSavePublicProfile}
            handleOpenPublicProfile={handleOpenPublicProfile}
            handleCopyPublicProfileLink={handleCopyPublicProfileLink}
          />

          <ProfileOverviewCard
            title="Musik & Hintergrund"
            subtitle="Steuert die Pfandpirat-Musik. Sie läuft weiter, während du zwischen den App-Bereichen wechselst."
          >
            <RadioSettingsPanel />
          </ProfileOverviewCard>

          <ProfileOverviewCard title="App-Einstellungen">
            <div className="pfp-profile-toggle-group">
              <label className="pfp-profile-toggle-row">
                <div>
                  <div className="pfp-profile-toggle-row__title">Challenge-Fokus</div>
                  <div className="pfp-profile-toggle-row__hint">
                    Blendet XP-Toasts, Kork-Stimme, Loot- und Belohnungsfenster auf diesem Gerät aus.
                    Punkte und Leaderboards laufen weiter.
                  </div>
                  {challengeFocusEnabled && (
                    <div className="pfp-profile-toggle-row__meta">
                      Aktiv: schneller sammeln ohne Erfolgs-Popups.
                    </div>
                  )}
                </div>
                <input
                  type="checkbox"
                  checked={challengeFocusEnabled}
                  onChange={(e) => handleChallengeFocusToggle(e.target.checked)}
                />
              </label>

              <label className="pfp-profile-toggle-row">
                <div>
                  <div className="pfp-profile-toggle-row__title">Pfandbon-Scan Beta</div>
                  <div className="pfp-profile-toggle-row__hint">
                    Aktiviere die Beta, wenn du Pfandbons fotografieren oder als Datei hochladen moechtest.
                    Ohne Beta bleibt die bisherige manuelle Einloesung aktiv. Nach der Beta wird der
                    Pfandbon-Nachweis schrittweise verpflichtend.
                  </div>
                  {receiptBetaEnabled && (
                    <div className="pfp-profile-toggle-row__meta">
                      Beta aktiv: Beim Bestaetigen pruefst du deinen Pfand mit Bon.
                    </div>
                  )}
                </div>
                <input
                  type="checkbox"
                  checked={receiptBetaEnabled}
                  disabled={!receiptRedemptionsAvailable || receiptBetaUpdating}
                  onChange={(e) => handleReceiptBetaToggle(e.target.checked)}
                />
              </label>

              <label className="pfp-profile-toggle-row">
                <div>
                  <div className="pfp-profile-toggle-row__title">Pfand-Alarm aktivieren</div>
                  <div className="pfp-profile-toggle-row__hint">{pushAlarmHint}</div>
                  {pushStatus.subscriptionId && (
                    <div className="pfp-profile-toggle-row__meta">
                      Geräte-ID endet auf {pushStatus.subscriptionId.slice(-8)}
                    </div>
                  )}
                </div>
                <input
                  type="checkbox"
                  checked={pushAlarmActive}
                  disabled={pushToggleDisabled}
                  onChange={(e) => handlePushAlarmToggle(e.target.checked)}
                />
              </label>

              {pushNeedsIosInstall && (
                <div className="pfp-profile-push-ios-help">
                  <strong>iPhone vorbereiten</strong>
                  <ol>
                    <li>Pfandpirat im iPhone-Browser öffnen.</li>
                    <li>Teilen-Symbol wählen und „Zum Home-Bildschirm“ antippen.</li>
                    <li>Pfandpirat über das neue Home-Screen-Symbol starten.</li>
                    <li>Im Profil den Pfand-Alarm erneut aktivieren.</li>
                  </ol>
                </div>
              )}

              {pushAlarmActive && (
                <div className="pfp-profile-push-test">
                  <button
                    type="button"
                    className="pfp-profile-push-test__button"
                    disabled={pushTestSending}
                    onClick={handleSendPushAlarmTest}
                  >
                    {pushTestSending ? 'Test wird gesendet ...' : 'Test-Push an dieses Gerät senden'}
                  </button>
                  {pushTestResult && (
                    <div className="pfp-profile-push-test__result">{pushTestResult}</div>
                  )}
                </div>
              )}
            </div>

            <label className="pfp-profile-toggle-row">
              <div>
                <div className="pfp-profile-toggle-row__title">XP-Gewinn Sound abspielen</div>
                <div className="pfp-profile-toggle-row__hint">Aktiviert den Sound bei XP-Gewinn.</div>
              </div>
              <input
                type="checkbox"
                checked={localStorage.getItem('xpSound') !== 'off'}
                onChange={(e) => localStorage.setItem('xpSound', e.target.checked ? 'on' : 'off')}
              />
            </label>

            <label className="pfp-profile-toggle-row">
              <div>
                <div className="pfp-profile-toggle-row__title">Käpt'n-Kork-Stimme abspielen</div>
                <div className="pfp-profile-toggle-row__hint">Aktiviert Korks gesprochene Sprüche bei XP-Toasts, Loot und Schatzmomenten.</div>
              </div>
              <input
                type="checkbox"
                checked={korkVoiceEnabled}
                onChange={(e) => handleKorkVoiceToggle(e.target.checked)}
              />
            </label>
          </ProfileOverviewCard>

        </div>
      )}

      <AvatarPickerModal
        isOpen={avatarPickerOpen}
        onClose={() => setAvatarPickerOpen(false)}
        avatars={avatars}
        selectedAvatarId={selectedAvatar?.id}
        onSelect={handleSelectAvatar}
        saving={avatarSaving}
      />
    </div>
  );
}
